import type { TProjectSettingsTag } from '@cascade/api-client/project-settings';
import type { TProjectData } from '@cascade/api-client/user';
import { skipToken, useQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLatest } from '#hooks/use-latest/index.ts';
import { useApi } from '#providers/api/index.ts';
import { useAuthentication } from '#providers/authentication/index.ts';
import { getQueryLoadState } from '#providers/query/index.ts';
import { ProjectsContext } from './Projects.context.ts';
import * as helpers from './Projects.helpers.ts';
import { getTagValues } from './tags/index.ts';
import type {
  TFeatureFlag,
  THelpdeskIdsByProject,
  THelpdeskRequest,
  TProjectRequest,
  TProjectsContextValue,
  TProjectsProviderProps,
} from './Projects.types.ts';

export const ProjectsProvider = ({
  scope,
  requestedProjectId,
  previousProjectId,
  requestedHelpdeskId,
  previousHelpdeskIds,
  children,
  onProjectChange,
  onHelpdeskIdsChange,
}: TProjectsProviderProps) => {
  const api = useApi();
  const { isAuthenticated, status, reason } = useAuthentication();
  const onProjectChangeRef = useLatest(onProjectChange);
  const onHelpdeskIdsChangeRef = useLatest(onHelpdeskIdsChange);
  const [currentProjectId, setCurrentProjectId] = useState<string>();
  const [lastProjectId, setLastProjectId] = useState(previousProjectId);

  const [appliedProjectRequest, setAppliedProjectRequest] =
    useState<TProjectRequest>();

  const [selectedHelpdeskIds, setSelectedHelpdeskIds] =
    useState<THelpdeskIdsByProject>(previousHelpdeskIds ?? {});

  const [appliedHelpdeskRequest, setAppliedHelpdeskRequest] =
    useState<THelpdeskRequest>();

  const isSignedOut = status === 'anonymous' && reason === 'signedOut';

  const listProjects = async (): Promise<TProjectData[]> => {
    if (scope === 'admin') {
      const { projects } = await api.user.getProjectsWhereAdmin({});

      return projects;
    }

    const { projects } = await api.user.getUserProjects({});

    return projects.map(helpers.mapToProjectData);
  };

  const projectsKey = helpers.getProjectsQueryKey(scope);

  const projectsQuery = useQuery({
    queryKey: projectsKey,
    queryFn: isAuthenticated ? listProjects : skipToken,
  });

  const listedProjects = projectsQuery.data;

  const projects = useMemo(() => listedProjects ?? [], [listedProjects]);

  const isNewProjectRequest =
    requestedProjectId !== appliedProjectRequest?.projectId;

  if (
    listedProjects &&
    (listedProjects !== appliedProjectRequest?.projects || isNewProjectRequest)
  ) {
    const projectIds = listedProjects.map((project) => project.id);
    const previous = currentProjectId ?? lastProjectId;

    const resolvedProjectId = helpers.resolveProjectId(projectIds, {
      requested: isNewProjectRequest ? requestedProjectId : undefined,
      previous,
    });

    setAppliedProjectRequest({
      projects: listedProjects,
      projectId: requestedProjectId,
    });

    setCurrentProjectId(resolvedProjectId);
    setLastProjectId(resolvedProjectId ?? previous);
  }

  const isLoadingProjects = isAuthenticated && projectsQuery.isPending;

  const { loadError: listError } = getQueryLoadState(projectsQuery);

  const { refetch: refetchProjects } = projectsQuery;

  const currentProject = useMemo(
    () => projects.find((project) => project.id === currentProjectId),
    [projects, currentProjectId],
  );

  const settingsKey = helpers.getSettingsQueryKey(currentProjectId);

  const settingsQuery = useQuery({
    queryKey: settingsKey,
    queryFn: currentProjectId
      ? () =>
          api.projectSettings.getProjectPublicSettings({
            projectId: currentProjectId,
          })
      : skipToken,
  });

  const settings = settingsQuery.data;

  const { loadError: settingsError } = getQueryLoadState(settingsQuery);

  const projectsError = listError ?? settingsError;
  const { refetch: refetchSettings } = settingsQuery;

  const helpdesks = useMemo(
    () => settings?.admin_of_helpdesks ?? [],
    [settings],
  );

  const selectHelpdesk = useCallback(
    (helpdeskId: string) => {
      const isAdministered = helpdesks.some(
        (helpdesk) => helpdesk.helpdesk_id === helpdeskId,
      );

      if (!currentProjectId || !isAdministered) {
        return;
      }

      setSelectedHelpdeskIds((selected) => ({
        ...selected,
        [currentProjectId]: helpdeskId,
      }));
    },
    [currentProjectId, helpdesks],
  );

  const isNewHelpdeskRequest =
    appliedHelpdeskRequest?.projectId !== requestedProjectId ||
    appliedHelpdeskRequest?.helpdeskId !== requestedHelpdeskId;

  const isRequestedProjectOpen =
    settings !== undefined &&
    currentProjectId !== undefined &&
    (requestedProjectId === undefined ||
      requestedProjectId === currentProjectId);

  if (isNewHelpdeskRequest && isRequestedProjectOpen) {
    setAppliedHelpdeskRequest({
      projectId: requestedProjectId,
      helpdeskId: requestedHelpdeskId,
    });

    if (requestedHelpdeskId !== undefined) {
      selectHelpdesk(requestedHelpdeskId);
    }
  }

  const selectedHelpdeskId = currentProjectId
    ? selectedHelpdeskIds[currentProjectId]
    : undefined;

  const currentHelpdesk = useMemo(
    () =>
      helpdesks.find(
        (helpdesk) => helpdesk.helpdesk_id === selectedHelpdeskId,
      ) ?? helpdesks[0],
    [helpdesks, selectedHelpdeskId],
  );

  const currentHelpdeskId = currentHelpdesk?.helpdesk_id;

  const isUserScope = scope === 'user';

  const isPrimaryUser = settings ? helpers.isPrimaryUser(settings) : false;
  const canReadCustomizableMessages = isUserScope && isPrimaryUser;

  const customizableMessagesKey =
    helpers.getCustomizableMessagesQueryKey(currentProjectId);

  const customizableMessagesQuery = useQuery({
    queryKey: customizableMessagesKey,
    queryFn:
      canReadCustomizableMessages && currentProjectId
        ? () =>
            api.projectSettings.getCustomizableMessages({
              project_id: currentProjectId,
            })
        : skipToken,
  });

  const customizableMessages = customizableMessagesQuery.data;

  const isLoadingCustomizableMessages =
    canReadCustomizableMessages && customizableMessagesQuery.isPending;

  const { loadError: customizableMessagesError } = getQueryLoadState(
    customizableMessagesQuery,
  );

  const tags = useMemo(() => settings?.session?.tags ?? [], [settings]);
  const tagValues = useMemo(() => getTagValues(tags), [tags]);

  const canEditTag = useCallback(
    (tag: TProjectSettingsTag) =>
      settings ? helpers.canEditTag(settings, tag) : false,
    [settings],
  );

  const editableTags = useMemo(
    () => tags.filter(canEditTag),
    [tags, canEditTag],
  );

  const hasUnsetRequiredTags = settings
    ? helpers.hasUnsetRequiredTags(settings)
    : false;

  const hasLockedRequiredTags = settings
    ? helpers.hasLockedRequiredTags(settings)
    : false;

  const reloadProjects = useCallback(async () => {
    if (settingsError) {
      await refetchSettings();

      return;
    }

    await refetchProjects();
  }, [settingsError, refetchSettings, refetchProjects]);

  const selectProject = useCallback(
    (projectId: string) => {
      if (projects.some((project) => project.id === projectId)) {
        setCurrentProjectId(projectId);
      }
    },
    [projects],
  );

  const requireProjectId = useCallback((): string => {
    if (!currentProjectId) {
      throw new Error('No project is selected');
    }

    return currentProjectId;
  }, [currentProjectId]);

  const isFeatureEnabled = useCallback(
    (flag: TFeatureFlag) =>
      settings ? helpers.isFeatureEnabled(settings, flag) : false,
    [settings],
  );

  useEffect(() => {
    if (currentProjectId === undefined) {
      return;
    }

    onProjectChangeRef.current?.(currentProjectId);
  }, [currentProjectId, onProjectChangeRef]);

  useEffect(() => {
    if (Object.keys(selectedHelpdeskIds).length === 0) {
      return;
    }

    onHelpdeskIdsChangeRef.current?.(selectedHelpdeskIds);
  }, [selectedHelpdeskIds, onHelpdeskIdsChangeRef]);

  useEffect(() => {
    if (!isSignedOut) {
      return;
    }

    setCurrentProjectId(undefined);
    setLastProjectId(undefined);
    setSelectedHelpdeskIds({});
    onProjectChangeRef.current?.(undefined);
    onHelpdeskIdsChangeRef.current?.({});
  }, [isSignedOut, onProjectChangeRef, onHelpdeskIdsChangeRef]);

  const canSelectProject = projects.length > 1;
  const canSelectHelpdesk = helpdesks.length > 1;
  const canEditTags = editableTags.length > 0;

  const canManageDependents = settings
    ? helpers.canManageDependents(settings)
    : false;

  const value = useMemo<TProjectsContextValue>(
    () => ({
      projects,
      isLoadingProjects,
      projectsError,
      currentProjectId,
      currentProject,
      helpdesks,
      currentHelpdeskId,
      currentHelpdesk,
      settings,
      customizableMessages,
      isLoadingCustomizableMessages,
      customizableMessagesError,
      tags,
      tagValues,
      editableTags,
      hasUnsetRequiredTags,
      hasLockedRequiredTags,
      isPrimaryUser,
      canSelectProject,
      canSelectHelpdesk,
      canEditTags,
      canManageDependents,
      reloadProjects,
      selectProject,
      selectHelpdesk,
      requireProjectId,
      isFeatureEnabled,
      canEditTag,
    }),
    [
      projects,
      isLoadingProjects,
      projectsError,
      currentProjectId,
      currentProject,
      helpdesks,
      currentHelpdeskId,
      currentHelpdesk,
      settings,
      customizableMessages,
      isLoadingCustomizableMessages,
      customizableMessagesError,
      tags,
      tagValues,
      editableTags,
      hasUnsetRequiredTags,
      hasLockedRequiredTags,
      isPrimaryUser,
      canSelectProject,
      canSelectHelpdesk,
      canEditTags,
      canManageDependents,
      reloadProjects,
      selectProject,
      selectHelpdesk,
      requireProjectId,
      isFeatureEnabled,
      canEditTag,
    ],
  );

  return (
    <ProjectsContext.Provider value={value}>
      <Fragment key={currentProjectId ?? ''}>{children}</Fragment>
    </ProjectsContext.Provider>
  );
};
